import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Share2, MessageCircle, CheckCircle, BedDouble, Waves, Building2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { getWhatsAppLink } from "@/data/sample-listings";
import { getTourShareUrl } from "@/lib/listings";
import { buildTourUrl } from "@/lib/tourEmbed";

const INSPECTION_MESSAGE =
  "Hello! I'd like to book an inspection for the Ultra-Luxury Estate in Asokoro, Abuja (₦8B) — 8 units of 7BR duplexes. I just viewed the 360° tour on Vista360.NG.";

const keyFeatures = [
  "8 Fully Detached 7-Bedroom Duplexes",
  "Basement levels in each unit",
  "5,000 sqm of prime gated land",
  "Fitted kitchens with stoves & AC",
  "Ideal for diplomatic / corporate housing",
  "Strong capital appreciation potential",
];

const HERO_TOUR_URL = "https://kuula.co/share/collection/7Mcwn";

export const HeroKuulaTour = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const shareTour = () => {
    const url = getTourShareUrl("asokoro-estate-7-5b-capture");
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast({ title: "Tour link copied!", description: "Share it on WhatsApp for a branded preview." });
      })
      .catch(() => {
        toast({ title: "Link copied!", description: url });
      });
  };

  return (
    <section className="relative w-full bg-foreground pt-16 md:pt-20">
      <div className="container-tight py-8 md:py-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-4 inline-flex flex-wrap items-center justify-center gap-2">
            <span className="badge-360 text-xs sm:text-sm">
              <svg viewBox="0 0 24 24" className="h-3 w-3 sm:h-4 sm:w-4" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                <path d="M2 12h20" />
              </svg>
              360° Live Tour
            </span>
            <span className="flex items-center gap-1.5 text-xs font-semibold text-secondary sm:text-sm">
              <CheckCircle className="h-3.5 w-3.5" />
              Vista360 Verified
            </span>
          </div>

          <h1 className="mb-3 font-display text-3xl font-bold leading-tight text-background sm:text-4xl md:text-5xl lg:text-6xl">
            Ultra-Luxury Estate For Sale
          </h1>
          <p className="mb-1 text-base font-semibold text-secondary sm:text-lg">
            8 Units of 7-Bedroom Duplexes · Asokoro, Abuja
          </p>
          <p className="text-sm text-background/60 sm:text-base">
            5,000 sqm · Gated Community · Prime Location
          </p>
        </motion.div>
      </div>

      <motion.div
        ref={containerRef}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.7 }}
        className="w-full"
      >
        <div className="relative h-[640px] w-full overflow-hidden">
          {isLoading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-foreground/95">
              <div className="mb-4 h-16 w-16 animate-spin rounded-full border-4 border-secondary/30 border-t-secondary" />
              <p className="font-medium text-background/70">Loading 360° Tour...</p>
            </div>
          )}

          {isInView && (
            <iframe
              src={buildTourUrl(HERO_TOUR_URL, false, { thumbs: "1" })}
              width="100%"
              height="640"
              frameBorder="0"
              allowFullScreen
              allow="xr-spatial-tracking; gyroscope; accelerometer; autoplay; fullscreen"
              title="Vista360 Verified — Asokoro Estate, Abuja"
              className="block border-0"
              onLoad={() => setIsLoading(false)}
            />
          )}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="container-tight flex flex-col items-center justify-center gap-4 py-8 sm:flex-row"
      >
        <Button
          size="lg"
          onClick={shareTour}
          className="gold-gradient w-full gap-2 font-semibold text-foreground shadow-gold sm:w-auto"
          data-testid="button-share-tour"
        >
          <Share2 className="h-5 w-5" />
          Share Tour
        </Button>
        <a
          href={getWhatsAppLink(INSPECTION_MESSAGE)}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full sm:w-auto"
          data-testid="button-book-inspection"
        >
          <Button size="lg" className="w-full gap-2 bg-[#25D366] font-semibold text-white hover:bg-[#1ebe5d]">
            <MessageCircle className="h-5 w-5" />
            Book Inspection
          </Button>
        </a>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="container-tight pb-14"
      >
        <div className="mx-auto max-w-4xl rounded-2xl border border-background/10 bg-background/5 p-6 md:p-10">
          <div className="mb-8 text-center">
            <h2 className="mb-2 font-display text-2xl font-bold uppercase tracking-wide text-background sm:text-3xl">
              Ultra-Luxury Estate — Asokoro
            </h2>
            <div className="mb-4 flex flex-wrap items-center justify-center gap-3 text-sm text-background/70">
              <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4 text-secondary" /> 8 × 7BR Duplexes</span>
              <span className="text-background/30">·</span>
              <span className="flex items-center gap-1.5"><Waves className="h-4 w-4 text-secondary" /> 5,000 sqm</span>
              <span className="text-background/30">·</span>
              <span className="flex items-center gap-1.5"><BedDouble className="h-4 w-4 text-secondary" /> ₦8B</span>
            </div>
            <p className="text-sm text-background/50">Asokoro, Abuja — Gated Community</p>
          </div>

          <div className="mb-8">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">Property Overview</p>
            <p className="leading-relaxed text-background/70">
              An exceptional opportunity to acquire a premium gated residential estate in the heart of Asokoro, Abuja's most prestigious district. Eight (8) units of fully detached 7-bedroom duplexes, each including a basement level, situated on 5,000 sqm of prime land within a secure gated community.
            </p>
          </div>

          <div className="mb-8">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">Features &amp; Finishes</p>
            <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
              {["Fully fitted & tastefully finished interiors", "Spacious living areas", "Modern kitchens with fitted stoves", "Air conditioning systems installed", "High-quality window blinds", "Ample parking space"].map((f) => (
                <div key={f} className="flex items-start gap-2 text-sm text-background/70">
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-secondary" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">Investment Potential</p>
            <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
              {["Ideal for diplomatic housing", "Suitable for corporate residential quarters", "High-end rental income potential", "Strong capital appreciation in prime Asokoro"].map((f) => (
                <div key={f} className="flex items-start gap-2 text-sm text-background/70">
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-secondary" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-primary">Key Features</p>
            <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
              {keyFeatures.map((feature) => (
                <div key={feature} className="flex items-start gap-2 text-sm text-background/70">
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-secondary" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-background/10 pt-6">
            <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-primary">Financial Summary</p>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl bg-background/5 p-4 text-center">
                <p className="mb-1 text-xs uppercase tracking-wider text-background/50">Asking Price</p>
                <p className="font-display text-lg font-bold text-secondary sm:text-xl">₦8,000,000,000</p>
              </div>
              <div className="rounded-xl bg-background/5 p-4 text-center">
                <p className="mb-1 text-xs uppercase tracking-wider text-background/50">Title</p>
                <div className="flex items-center justify-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-secondary" />
                  <p className="text-sm font-semibold text-background">Secure &amp; Verifiable</p>
                </div>
              </div>
              <div className="rounded-xl bg-background/5 p-4 text-center">
                <p className="mb-1 text-xs uppercase tracking-wider text-background/50">Inspection</p>
                <p className="text-sm font-semibold text-background">Strictly by Appointment</p>
              </div>
            </div>

            <div className="mt-6 text-center">
              <a
                href={getWhatsAppLink(INSPECTION_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="button-book-inspection-bottom"
              >
                <Button size="lg" className="gap-2 bg-[#25D366] font-semibold text-white hover:bg-[#1ebe5d]">
                  <MessageCircle className="h-5 w-5" />
                  Book Inspection via WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
